import { useId, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Layers, Plus } from 'lucide-react';
import { CATEGORY_COLOURS, type CategoryColour } from '@fint/validation';
import { api, ApiError } from '@/lib/api';
import { EMOJI_PALETTE, emojiProblem } from '@/lib/emoji';
import { cn } from '@/lib/cn';
import { useActiveOrg } from '@/stores/auth';
import { usePermissions } from '@/hooks/usePermissions';
import { useEscapeToClose } from '@/hooks/useEscapeToClose';
import { PageHeader } from '@/app/DashboardLayout';
import { Button, Card, ColourChip, ColourPicker, EmptyState, ErrorState, Input, Sheet, SheetBody, SheetFooter, SheetForm, SheetHeader, SkeletonTable, Table, TBody, TD, TH, THead, TR } from '@/features/common/ui';

interface CategoryRow {
  id: string;
  name: string;
  description: string | null;
  colour: string;
  parentId: string | null;
  excludedFromTotals: boolean;
  interBook: boolean;
  /** One picture for the quick-add tile. Null until somebody picks one. */
  emoji: string | null;
  sortOrder: number;
  isActive: boolean;
}

type Kind = 'expense' | 'income';

interface CategoryInput {
  id?: string;
  name: string;
  colour: CategoryColour;
  excludedFromTotals: boolean;
  interBook: boolean;
  /** Empty string clears it - the server stores that as NULL. */
  emoji: string;
}

/**
 * The two category lists.
 *
 * ── WHY THIS SCREEN IS THE POINT OF THE WHOLE FEATURE ──
 * The owner asked for tags. What their spreadsheet actually classifies money by
 * is a single-valued, mandatory, colour-coded list on each side of the ledger -
 * which is exactly what `expense_categories` has been since the first release,
 * unseen. Nothing in the product ever showed them their own categories back, so
 * they reached for the word they had. This is the surface that was missing; the
 * income list beside it is the half that genuinely did not exist.
 *
 * Two tabs rather than one list, because mixing them is precisely what produced
 * the `REV-` prefix on their income buckets: a namespace hand-rolled to keep two
 * kinds of money apart inside one flat list. Give somebody two lists and the
 * prefix has no job.
 */
export function CategoriesPage() {
  const org = useActiveOrg();
  const perms = usePermissions();
  const queryClient = useQueryClient();

  const [kind, setKind] = useState<Kind>('expense');
  const [editing, setEditing] = useState<CategoryRow | 'new' | null>(null);

  const orgId = org?.id;
  const both = useQuery({
    queryKey: ['categories', orgId],
    enabled: !!orgId,
    queryFn: () => api.get<{ expense: CategoryRow[]; income: CategoryRow[] }>('/categories'),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['categories', orgId] });
    // The dashboard's breakdowns are built from these, so a recoloured category
    // that only changes on the next hard refresh reads as a bug.
    void queryClient.invalidateQueries({ queryKey: ['dashboard-breakdown', orgId] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard-overview', orgId] });
  };

  const save = useMutation({
    mutationFn: (input: CategoryInput) => {
      // The same body on create and on edit. Create used to send name and
      // colour only, so a category made with "leave out of my totals" ticked
      // was created counted, and the tick had to be made a second time.
      const body = {
        name: input.name,
        colour: input.colour,
        excludedFromTotals: input.excludedFromTotals,
        interBook: input.interBook,
        emoji: input.emoji,
      };
      if (input.id) {
        const path = kind === 'income' ? 'income-categories' : 'expense-categories';
        return api.patch<CategoryRow>(`/${path}/${input.id}`, body);
      }
      return kind === 'income'
        ? api.post<CategoryRow>('/income-categories', body)
        : api.post<CategoryRow>('/expenses/categories', body);
    },
    onSuccess: () => {
      setEditing(null);
      invalidate();
      // Every picker that names a category carries its picture now, and a
      // picture changed here must change there without a hard refresh.
      void queryClient.invalidateQueries({ queryKey: ['expense-categories', orgId] });
      void queryClient.invalidateQueries({ queryKey: ['income-categories', orgId] });
      void queryClient.invalidateQueries({ queryKey: ['expenses', orgId] });
    },
  });

  const rows = (kind === 'income' ? both.data?.income : both.data?.expense) ?? [];

  return (
    <>
      <PageHeader
        title="Categories"
        subtitle="What kind of money it was. Every expense has one, and it is what the dashboard breaks your money down by."
        actions={
          <Button onClick={() => setEditing('new')}>
            <Plus className="h-4 w-4" /> New category
          </Button>
        }
      />
      {/* The same gutter every other screen has. Without it the content sat
          flush against the sidebar, on these four pages and no others. */}
      <div className="space-y-4 px-4 pb-10 sm:px-5">
        <div className="flex gap-1.5">
          {(['expense', 'income'] as const).map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={kind === option}
              onClick={() => setKind(option)}
              className={
                kind === option
                  ? 'rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary'
                  : 'rounded-full px-3 py-1 text-xs font-medium text-muted-foreground hover:bg-muted'
              }
            >
              {option === 'expense' ? 'Expense' : 'Income'} ·{' '}
              {(option === 'income' ? both.data?.income : both.data?.expense)?.length ?? 0}
            </button>
          ))}
        </div>

        {both.isPending ? <SkeletonTable rows={6} /> : null}
        {both.isError ? <ErrorState error={both.error} onRetry={() => { void both.refetch(); }} /> : null}

        {both.isSuccess && rows.length === 0 ? (
          <EmptyState
            icon={Layers}
            title={`No ${kind} categories yet`}
            description="Add one and every record you file can say what kind of money it was."
            action={<Button onClick={() => setEditing('new')}>Create one</Button>}
          />
        ) : null}

        {rows.length > 0 ? (
          <Card>
            <Table>
              <THead>
                <TR>
                  <TH>Category</TH>
                  <TH>In your totals</TH>
                  <TH className="text-right">Actions</TH>
                </TR>
              </THead>
              <TBody>
                {rows.map((category) => (
                  <TR key={category.id}>
                    <TD>
                      <ColourChip colour={category.colour} emoji={category.emoji} name={category.name} />
                    </TD>
                    <TD className="text-muted-foreground">
                      {category.excludedFromTotals ? (
                        // Badged rather than hidden: an excluded category is still
                        // pickable and still on its records, it is simply outside
                        // the headline figure - and nobody should be surprised by
                        // that at the moment they pick it.
                        <span>Left out</span>
                      ) : category.interBook ? (
                        // Counted here; taken out of the Overview's total across
                        // books, where the same money is on the other side.
                        <span>Counted · between your books</span>
                      ) : (
                        <span>Counted</span>
                      )}
                    </TD>
                    <TD className="text-right">
                      {perms.can('settings', 'manage') ? (
                        <Button variant="ghost" size="sm" onClick={() => setEditing(category)}>
                          Edit
                        </Button>
                      ) : null}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </Card>
        ) : null}

        {editing ? (
          <CategoryComposer
            kind={kind}
            category={editing === 'new' ? undefined : editing}
            canExclude={perms.can('settings', 'manage')}
            onClose={() => setEditing(null)}
            onSubmit={(input) => save.mutate(input)}
            pending={save.isPending}
            error={save.error instanceof ApiError ? save.error : undefined}
          />
        ) : null}
      </div>
    </>
  );
}

function CategoryComposer({
  kind,
  category,
  canExclude,
  onClose,
  onSubmit,
  pending,
  error,
}: {
  kind: Kind;
  category?: CategoryRow;
  canExclude: boolean;
  onClose: () => void;
  onSubmit: (input: CategoryInput) => void;
  pending: boolean;
  error?: ApiError;
}) {
  useEscapeToClose(onClose);
  const [name, setName] = useState(category?.name ?? '');
  const [colour, setColour] = useState<CategoryColour>(
    (CATEGORY_COLOURS.find((c) => c === category?.colour) ?? CATEGORY_COLOURS[1]) as CategoryColour,
  );
  const [excluded, setExcluded] = useState(category?.excludedFromTotals ?? false);
  const [interBook, setInterBook] = useState(category?.interBook ?? false);
  const [emoji, setEmoji] = useState(category?.emoji ?? '');
  const emojiError = emojiProblem(emoji);

  return (
    <Sheet labelledBy="category-sheet-title" onClose={onClose}>
      <SheetHeader titleId="category-sheet-title" title={category ? 'Edit category' : kind === 'income' ? 'New income category' : 'New expense category'} onClose={onClose} />
      <SheetForm
        onSubmit={(event) => {
          event.preventDefault();
          if (emojiError) return;
          onSubmit({
            id: category?.id,
            name,
            colour,
            excludedFromTotals: excluded,
            interBook,
            emoji: emoji.trim(),
          });
        }}
      >
        <SheetBody>
          <div className="space-y-1">
            <label htmlFor="cat-name" className="text-sm font-medium">
              Name
            </label>
            <span aria-hidden="true" className="text-destructive"> *</span>
            <Input
              id="cat-name"
              // The sheet opens for one purpose; landing focus anywhere else
              // makes a keyboard user tab past a heading and a close button to
              // reach the only field that matters.
              autoFocus
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={120}
              required
            />
          </div>

          <div className="space-y-1.5">
            <span className="text-sm font-medium">Colour</span>
            <ColourPicker value={colour} onChange={setColour} />
            <p className="text-xs text-muted-foreground">
              The same colour is used in the chip and in the bar on your dashboard, so a category looks
              the same everywhere.
            </p>
          </div>

          <EmojiField value={emoji} onChange={setEmoji} error={emojiError ?? error?.fieldError('emoji')} />

          {canExclude ? (
            <div className="space-y-1">
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={excluded}
                  onChange={(event) => setExcluded(event.target.checked)}
                  // The explanation is `aria-describedby`, not part of the
                  // label - otherwise the checkbox's accessible name is a
                  // paragraph.
                  aria-describedby="cat-excluded-hint"
                  className="mt-0.5"
                />
                <span className="font-medium">Leave this out of my totals</span>
              </label>
              <p id="cat-excluded-hint" className="text-xs text-muted-foreground">
                For money that moved but is not really {kind === 'income' ? 'income' : 'spending'} - a
                transfer to savings, or something you were reimbursed for. It still shows in your lists,
                still affects your bank balance, and is still on every tax return. The dashboard reports it
                separately rather than hiding it.
              </p>
            </div>
          ) : null}

          {canExclude ? (
            <div className="space-y-1">
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={interBook}
                  onChange={(event) => setInterBook(event.target.checked)}
                  aria-describedby="cat-inter-book-hint"
                  className="mt-0.5"
                />
                <span className="font-medium">This is money between my books</span>
              </label>
              <p id="cat-inter-book-hint" className="text-xs text-muted-foreground">
                {kind === 'income'
                  ? 'Paid to this book by another of your books - a salary or partner remuneration from your own company. It is real income here and stays in this book\u2019s figures. The Overview, which adds your books together, leaves it out of the total, because there it is the same money as the other book\u2019s expense.'
                  : 'Paid by this book to another of your books - a salary or partner remuneration to yourself. It is a real cost here and stays in this book\u2019s figures. The Overview, which adds your books together, leaves it out of the total, because there it is the same money as the other book\u2019s income.'}
              </p>
            </div>
          ) : null}

          {error ? (
            // Announced. The 409 "that tag is on 47 records" is the whole reason
            // the button did nothing, and it renders below the fold of a form.
            <p role="alert" className="text-sm text-destructive">
              {error.message}
            </p>
          ) : null}
        </SheetBody>
        <SheetFooter>
          <Button type="submit" disabled={pending || !name.trim() || !!emojiError}>
            {pending ? 'Saving…' : category ? 'Save changes' : 'Create category'}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </SheetFooter>
      </SheetForm>
    </Sheet>
  );
}

/**
 * The emoji picker: a curated palette, "none", and a box for any other.
 *
 * ── WHY A PALETTE AND A BOX, NOT A FULL EMOJI KEYBOARD ──
 * Every phone already has one. What a phone does not have is forty tiles that
 * cover what a small business spends on, one tap each, no search - which is
 * the whole of the decision for most categories. The box is for the one in
 * ten that the palette cannot hold, and the rule beside it (`emojiProblem`)
 * says in a sentence why two pizzas or a seven-person family will not save,
 * rather than letting the server say 400.
 *
 * A radiogroup with a roving tabindex, as `ColourPicker` is: one tab stop,
 * arrows to move. The box is its own stop after the group. The tiles carry
 * the picture AND a name (`aria-label`), because a briefcase is announced as
 * "briefcase" by some readers and as nothing by others.
 */
function EmojiField({
  value,
  onChange,
  error,
}: {
  value: string;
  onChange: (emoji: string) => void;
  error?: string;
}) {
  const boxId = useId();
  const inPalette = value === '' || EMOJI_PALETTE.some((t) => t.emoji === value);
  // "None" first, then the palette. The index the roving focus moves over.
  const tiles: Array<{ emoji: string; label: string }> = [{ emoji: '', label: 'None' }, ...EMOJI_PALETTE];
  const current = inPalette ? tiles.findIndex((t) => t.emoji === value) : -1;

  const move = (delta: number) => {
    const from = current === -1 ? 0 : current;
    const next = tiles[(from + delta + tiles.length) % tiles.length];
    if (!next) return;
    onChange(next.emoji);
    document.getElementById(`${boxId}-tile-${next.emoji || 'none'}`)?.focus();
  };

  return (
    <div className="space-y-1.5">
      <span className="text-sm font-medium">Emoji</span>
      <div
        role="radiogroup"
        aria-label="Emoji"
        className="flex flex-wrap gap-1"
        onKeyDown={(event) => {
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
            event.preventDefault();
            move(1);
          } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
            event.preventDefault();
            move(-1);
          }
        }}
      >
        {tiles.map((tile, index) => {
          const selected = inPalette && index === current;
          return (
            <button
              key={tile.label}
              id={`${boxId}-tile-${tile.emoji || 'none'}`}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={tile.emoji ? `${tile.label} ${tile.emoji}` : 'No emoji'}
              title={tile.label}
              // The first tile is the stop when nothing in the palette is
              // chosen, so the group is always reachable by keyboard.
              tabIndex={selected || (current === -1 && index === 0) ? 0 : -1}
              onClick={() => onChange(tile.emoji)}
              className={cn(
                // 44px on a phone, as every other tap target here; a grid of
                // 32px pictures is a grid of missed taps.
                'flex size-9 items-center justify-center rounded-md border text-lg leading-none transition-colors touch:size-11 touch:text-xl',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                selected
                  ? 'border-primary bg-primary/10'
                  : 'border-input bg-background hover:bg-accent',
              )}
            >
              {tile.emoji ? tile.emoji : <span className="text-xs text-muted-foreground">none</span>}
            </button>
          );
        })}
      </div>
      <div className="flex items-center gap-2">
        <label htmlFor={boxId} className="shrink-0 text-xs text-muted-foreground">
          Or any other
        </label>
        <Input
          id={boxId}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          aria-invalid={error ? true : undefined}
          aria-describedby={`${boxId}-hint`}
          // 16 is the column's limit in code units; the real check is the
          // one-grapheme rule below, which counts what a person sees.
          maxLength={16}
          className="max-w-[6rem] text-center text-lg"
          inputMode="text"
        />
      </div>
      <p
        id={`${boxId}-hint`}
        className={cn('text-xs', error ? 'text-destructive' : 'text-muted-foreground')}
        role={error ? 'alert' : undefined}
      >
        {error ?? 'One emoji. It goes on the quick-add tile and before the name wherever this category is shown.'}
      </p>
    </div>
  );
}
